import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { setUser } from '../../redux/actions/user'
import { getUserInfo } from '../../api/auth'
import { leaveFromFamilyPlan } from '../../api/user'
import { shareSubscription } from '../../api/trial'
import {
  apiError,
  cancelSubscription,
  changePlan,
  resumeSubscription,
  upgradeCheckout,
} from '../../api/account'
import { openCheckout } from '../../utils/chargebee'
import { MORSEL_PRO, MORSEL_PRO_FAMILY } from '../../utils/constants'
import routes from '../../routers'
import { cardBrand, day, familyButtonText, priceText } from './format'
import { FAMILY_EXTRA, FEATURES, Perks } from './perks'

import styles from './AccountSettings.module.css'

// Reload the signed-in user so the rest of the app sees the new plan straight away
const useRefreshUser = () => {
  const dispatch = useDispatch()
  return () =>
    getUserInfo()
      .then(({ data }) => dispatch(setUser(data)))
      .catch(() => null)
}

const FamilySharing = ({ billing, reload }) => {
  const dispatch = useDispatch()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const shared = billing.shared_users || []
  const full = shared.length >= (billing.max_shared || 6) - 1

  const save = (emails) => {
    setBusy(true)
    setError('')
    return shareSubscription({ emails })
      .then(({ data }) => {
        dispatch(setUser(data))
        setEmail('')
        return reload()
      })
      .catch((err) => {
        const bad = err?.response?.data?.data
        setError(
          Array.isArray(bad)
            ? `${bad.join(', ')} already has a Morsel subscription of their own.`
            : apiError(err, 'We could not update your family plan. Please try again.')
        )
      })
      .finally(() => setBusy(false))
  }

  const add = (e) => {
    e.preventDefault()
    const value = email.trim().toLowerCase()
    if (!/^\S+@\S+\.\S+$/.test(value)) return setError('Enter a valid email')
    if (shared.some((u) => u.email === value)) return setError('That email is already on your plan')
    save([...shared.map((u) => u.email), value])
  }

  return (
    <div>
      <h3 className={styles.h3}>Shared with your family</h3>
      <p className={styles.small} style={{ marginTop: 0 }}>
        Family Pro covers up to {billing.max_shared || 6} people, including you. Everyone gets
        everything in Pro.
      </p>
      <div className={styles.list}>
        {shared.map((u) => (
          <div key={u.email} className={styles.listRow}>
            <span className={styles.grow}>{u.email}</span>
            <span className={`${styles.pill} ${u.status ? styles.pillWarn : ''}`}>
              {u.status ? 'Invite sent' : 'Joined'}
            </span>
            <button
              type="button"
              className={styles.btnLinkDanger}
              disabled={busy}
              onClick={() => save(shared.filter((x) => x.email !== u.email).map((x) => x.email))}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      {full ? (
        <p className={styles.small}>Your plan is full.</p>
      ) : (
        <form
          onSubmit={add}
          className={styles.row}
          style={{ marginTop: 12, alignItems: 'flex-end' }}
        >
          <label className={`${styles.label} ${styles.grow}`}>
            Share with another email
            <input
              className={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button className={styles.btnDark} disabled={busy}>
            Share
          </button>
        </form>
      )}
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  )
}

const SubscriptionSection = ({ billing, failed, reload }) => {
  const giftsOn = useSelector((state) => !!state.user?.data?.features?.gifts)
  const refreshUser = useRefreshUser()
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [confirmSwitch, setConfirmSwitch] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const run = (fn, fallback, after) => {
    setBusy(true)
    setError('')
    return fn()
      .then(async () => {
        await reload()
        await refreshUser()
        after?.()
      })
      .catch((err) => setError(apiError(err, fallback)))
      .finally(() => setBusy(false))
  }

  const upgrade = (planId) => {
    setBusy(true)
    setError('')
    upgradeCheckout(planId)
      .then(({ data }) => openCheckout(billing.chargebee_site, data))
      .then(async ({ paid }) => {
        if (!paid) return
        setNotice('Welcome to Morsel Pro! Every recipe is unlocked.')
        await reload()
        await refreshUser()
      })
      .catch((err) =>
        setError(apiError(err, 'Checkout is not available right now. Please try again.'))
      )
      .finally(() => setBusy(false))
  }

  const head = (pill) => (
    <div className={styles.cardHead}>
      <h2 id="subscription-title" className={styles.h2}>
        Subscription
      </h2>
      {pill}
    </div>
  )

  let body
  if (failed || billing?.unavailable) {
    body = (
      <p className={styles.error}>
        Your subscription details are not available right now. Please refresh the page in a minute.
      </p>
    )
  } else if (!billing) {
    body = <p className={styles.small}>Loading...</p>
  } else if (billing.shared_from && !billing.subscription) {
    body = (
      <>
        <p className={styles.lead}>
          You are on {billing.shared_from.name || 'a family member'}'s Family Pro plan, so you get
          everything in Pro.
        </p>
        <Perks items={FEATURES} />
        <div className={styles.row} style={{ marginTop: 20 }}>
          <button
            className={styles.btn}
            disabled={busy}
            onClick={() =>
              run(
                leaveFromFamilyPlan,
                'We could not take you off the family plan. Please try again.'
              )
            }
          >
            Leave the family plan
          </button>
        </div>
      </>
    )
  } else if (billing.gift_pro && !(billing.subscription && !billing.subscription.cancelled)) {
    const family = billing.gift_pro.plan_id === MORSEL_PRO_FAMILY
    body = (
      <>
        <p className={styles.lead}>
          You have Morsel {family ? 'Family Pro' : 'Pro'} as a gift, through{' '}
          {day(billing.gift_pro.until)}. There's nothing to pay. After that you go back to the free
          plan, and your recipes stay.
        </p>
        <Perks items={FEATURES} />
        {family ? <FamilySharing billing={billing} reload={reload} /> : null}
      </>
    )
  } else if (billing.free_pro && !(billing.subscription && !billing.subscription.cancelled)) {
    body = (
      <>
        <p className={styles.lead}>
          You have Morsel Pro, free from the Morsel team, through {day(billing.free_pro.until)}.
          There's nothing to pay. After that you go back to the free plan, and your recipes stay.
        </p>
        <Perks items={FEATURES} />
      </>
    )
  } else if (billing.subscription && !billing.subscription.cancelled) {
    const sub = billing.subscription
    const family = sub.plan_id === MORSEL_PRO_FAMILY
    const other = billing.plans.find((p) => p.id === (family ? MORSEL_PRO : MORSEL_PRO_FAMILY))
    const ending = sub.status === 'non_renewing'
    const card = billing.card
      ? ` on your ${cardBrand(billing.card.brand)} ending ${billing.card.last4}`
      : ''
    body = (
      <>
        <p className={styles.lead}>Managed right here. No separate billing site.</p>
        <div className={styles.planGrid}>
          <div className={styles.planCard}>
            <p className={styles.eyebrow}>Your plan</p>
            <p className={styles.planName}>{sub.plan_name}</p>
            <p style={{ margin: '0 0 8px' }}>
              {priceText(billing.plans.find((p) => p.id === sub.plan_id))}
            </p>
            <p className={styles.small}>
              {ending
                ? `Ends ${day(sub.ends_at)}. You will not be charged again.`
                : sub.renews_at
                  ? `Renews ${day(sub.renews_at)}${card}.`
                  : ''}
            </p>
            <div className={styles.list} style={{ marginTop: 'auto', paddingTop: 16 }}>
              {ending ? (
                <button
                  className={styles.btnPrimary}
                  disabled={busy}
                  onClick={() => run(resumeSubscription, 'We could not resume your subscription.')}
                >
                  Keep my subscription
                </button>
              ) : (
                <>
                  {other ? (
                    <button
                      className={styles.btnDark}
                      disabled={busy}
                      onClick={() => setConfirmSwitch(true)}
                    >
                      {family ? 'Switch to Morsel Pro (just me)' : 'Switch to Family Pro'}
                    </button>
                  ) : null}
                  <button
                    className={styles.btnLinkDanger}
                    disabled={busy}
                    onClick={() => setConfirmCancel(true)}
                  >
                    Cancel subscription
                  </button>
                </>
              )}
            </div>
          </div>
          <div>
            <p className={styles.eyebrow} style={{ marginBottom: 14 }}>
              Included in your plan
            </p>
            <Perks items={family ? [FAMILY_EXTRA, ...FEATURES] : FEATURES} />
          </div>
        </div>

        {confirmSwitch && other ? (
          <div className={styles.alert} style={{ borderColor: '#37190c', background: '#fffdf0' }}>
            <b>Switch to {other.name}?</b>
            <p className={styles.small}>
              {other.name} is {priceText(other)}. The change starts today, and you are charged or
              credited the difference for the rest of this year{card}.
              {family && (billing.shared_users || []).length
                ? ` The ${billing.shared_users.length} people on your Family Pro plan go back to the free plan.`
                : ''}
            </p>
            <div className={styles.row}>
              <button
                className={styles.btnDark}
                disabled={busy}
                onClick={() =>
                  run(
                    () => changePlan(other.id),
                    'We could not change your plan. Please check your card and try again.',
                    () => setConfirmSwitch(false)
                  )
                }
              >
                Switch plan
              </button>
              <button className={styles.btn} onClick={() => setConfirmSwitch(false)}>
                Keep my plan
              </button>
            </div>
          </div>
        ) : null}

        {confirmCancel ? (
          <div className={styles.alert}>
            <b>Cancel your Morsel subscription?</b>
            <p className={styles.small}>
              You keep full access until {day(sub.renews_at)}. Your recipes stay safe, and you can
              resume any time before then. After that, your 10 most recent recipes stay open and the
              rest are locked until you come back.
            </p>
            <div className={styles.row}>
              <button
                className={styles.btnDanger}
                disabled={busy}
                onClick={() =>
                  run(cancelSubscription, 'We could not cancel your subscription.', () =>
                    setConfirmCancel(false)
                  )
                }
              >
                Cancel at end of term
              </button>
              <button className={styles.btn} onClick={() => setConfirmCancel(false)}>
                Keep my plan
              </button>
            </div>
          </div>
        ) : null}

        {family ? <FamilySharing billing={billing} reload={reload} /> : null}
        <p className={styles.small} style={{ marginTop: 20 }}>
          Want to give a year of Morsel to someone? Email{' '}
          <a href="mailto:info@getmorsel.com?subject=Gift%20a%20Morsel%20subscription">
            info@getmorsel.com
          </a>{' '}
          and we will set it up.
        </p>
      </>
    )
  } else {
    const usage = billing.usage || { used: 0, limit: 10, locked: 0 }
    const pro = billing.plans.find((p) => p.id === MORSEL_PRO)
    const familyPlan = billing.plans.find((p) => p.id === MORSEL_PRO_FAMILY)
    const left = Math.max(usage.limit - usage.used, 0)
    body = (
      <>
        <p className={styles.lead}>You are on the free plan.</p>
        <div className={styles.upsell}>
          <div className={styles.upsellDark}>
            <p className={styles.eyebrow}>Free plan</p>
            <h3>Room for every family recipe.</h3>
            <div className={styles.row} style={{ justifyContent: 'space-between' }}>
              <span>
                {Math.min(usage.used, usage.limit)} of {usage.limit} free recipes used
              </span>
              <span>{usage.locked ? `${usage.locked} locked` : `${left} left`}</span>
            </div>
            <div
              className={styles.meter}
              role="img"
              aria-label={`${usage.used} of ${usage.limit} recipes used`}
            >
              <div style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }} />
            </div>
            <p style={{ margin: '6px 0' }}>
              {usage.locked
                ? `Your ${usage.locked} older recipes are locked, not deleted. Go Pro to open them all again.`
                : 'Go Pro for unlimited recipes. Everything you have already added stays right where it is.'}
            </p>
            {pro ? (
              <p className={styles.small} style={{ color: '#f7f5ad', margin: 0 }}>
                {priceText(pro)}. Cancel any time.
              </p>
            ) : null}
            <div className={styles.row} style={{ marginTop: 8 }}>
              <button
                className={styles.btnGold}
                disabled={busy}
                onClick={() => upgrade(MORSEL_PRO)}
              >
                Go Pro
              </button>
              {familyPlan ? (
                <button
                  className={styles.btnGhostLight}
                  disabled={busy}
                  onClick={() => upgrade(MORSEL_PRO_FAMILY)}
                >
                  {familyButtonText(familyPlan)}
                </button>
              ) : null}
            </div>
          </div>
          <div className={styles.upsellLight}>
            <p className={styles.eyebrow} style={{ marginBottom: 14 }}>
              What you get with Pro
            </p>
            <Perks items={FEATURES} />
          </div>
        </div>
      </>
    )
  }

  const sub = billing?.subscription
  const pill =
    !sub || sub.cancelled ? null : (
      <span className={`${styles.pill} ${sub.status === 'non_renewing' ? styles.pillWarn : ''}`}>
        {sub.status === 'non_renewing' ? `Cancels ${day(sub.ends_at)}` : 'Active'}
      </span>
    )

  return (
    <section id="subscription" className={styles.card} aria-labelledby="subscription-title">
      {head(pill)}
      {notice ? <p className={styles.saved}>{notice}</p> : null}
      {body}
      {error ? <p className={styles.error}>{error}</p> : null}
      {giftsOn ? (
        <p className={styles.small} style={{ marginTop: 16 }}>
          <Link to={routes.gift}>Give Morsel as a gift</Link> or{' '}
          <Link to={routes.redeemGift}>open a gift code</Link>
        </p>
      ) : null}
    </section>
  )
}

export default SubscriptionSection
